import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@/hooks/UserContext';
import { usePreferredTimeZone } from '@/hooks/usePreferredTimeZone';
import { useZonedNow } from '@/hooks/useZonedNow';
import { resolveWeekStart } from '@/i18n/weekStart';
import { useTimeProgress } from './useTimeProgress';
import { getTimeMode } from '../util/timeProgress';
import { setLocalStorage } from '@/utils/localStrage';
import { usePanelLoading } from '@/components/PanelLoadingBoundary';
import { useResponsiveFontSize } from '@/hooks/useResponsiveFontSize';

interface UseTimePanelControllerOptions {
    storageKey: string;
    fontSizeCoefficient?: number;
}

/**
 * タイムパネルのコントローラー
 */
export const useTimePanelController = ({
    storageKey,
    fontSizeCoefficient,
}: UseTimePanelControllerOptions) => {
    const { userInfo } = useUser();
    const [isCountdown, setIsCountdown] = useState<boolean>(false);
    const [isInitializing, setIsInitializing] = useState<boolean>(true);
    const { setLoading } = usePanelLoading();

    const containerRef = useRef<HTMLDivElement>(null);
    const fontSize = useResponsiveFontSize(containerRef, fontSizeCoefficient);

    const weekStartsOn = useMemo(() => resolveWeekStart(userInfo?.weekStart), [userInfo?.weekStart]);
    const timeZone = usePreferredTimeZone();
    const now = useZonedNow(timeZone);
    const progress = useTimeProgress(now, isCountdown, weekStartsOn);

    const lifeProgress = useMemo(() => {
        if (!userInfo?.birthYear) return null;
        const birthYear = Number(userInfo.birthYear);
        if (Number.isNaN(birthYear)) return null;

        // 誕生日を1月1日と仮定して日単位で計算
        const birthDate = now.year(birthYear).startOf('year');
        const daysLived = Math.max(now.diff(birthDate, 'day'), 0);

        // 80年 = 80 × 365.25日（閏年考慮）
        const totalDays = 80 * 365.25;
        if (totalDays <= 0) return null;
        const ratio = Math.min(Math.max(daysLived / totalDays, 0), 1);
        // 小数点3桁で表示（1日で約0.0034%変動するため、3桁で毎日変化が見える）
        return Number((ratio * 100).toFixed(3));
    }, [now, userInfo?.birthYear]);

    useEffect(() => {
        if (!userInfo) {
            setIsInitializing(true);
        }
    }, [userInfo]);

    useEffect(() => {
        if (!userInfo?.userId) return;

        let cancelled = false;

        const fetchMode = async () => {
            setIsInitializing(true);
            try {
                const storedMode = await getTimeMode(userInfo.userId, storageKey);
                if (!cancelled) {
                    setIsCountdown(storedMode === 'countdown');
                }
            } finally {
                if (!cancelled) {
                    setIsInitializing(false);
                }
            }
        };

        void fetchMode();

        return () => {
            cancelled = true;
        };
    }, [storageKey, userInfo?.userId]);

    useEffect(() => {
        if (!userInfo) {
            setLoading(true);
            return;
        }
        setLoading(isInitializing);
    }, [isInitializing, setLoading, userInfo]);

    const toggleTimeMode = useCallback(async () => {
        if (!userInfo?.userId) return;
        const nextMode = isCountdown ? 'normal' : 'countdown';
        setIsCountdown((prev) => !prev);
        await setLocalStorage(userInfo.userId, storageKey, nextMode);
    }, [isCountdown, storageKey, userInfo?.userId]);

    const { currentHour, currentMinute, currentSecond } = useMemo(() => {
        const pad = (value: number) => value.toString().padStart(2, '0');
        const totalSecondsInDay = 24 * 60 * 60;
        const diffFromStartOfDay = now.diff(now.startOf('day'), 'second');
        const boundedDiff = Number.isNaN(diffFromStartOfDay)
            ? 0
            : Math.min(Math.max(diffFromStartOfDay, 0), totalSecondsInDay);

        if (isCountdown) {
            const remaining = Math.max(totalSecondsInDay - boundedDiff, 0);
            const hours = Math.floor(remaining / 3600);
            const minutes = Math.floor((remaining % 3600) / 60);
            const seconds = remaining % 60;
            return {
                currentHour: pad(hours),
                currentMinute: pad(minutes),
                currentSecond: pad(seconds),
            };
        }

        const hours = Math.floor(boundedDiff / 3600);
        const minutes = Math.floor((boundedDiff % 3600) / 60);
        const seconds = boundedDiff % 60;
        return {
            currentHour: pad(hours),
            currentMinute: pad(minutes),
            currentSecond: pad(seconds),
        };
    }, [isCountdown, now]);

    return {
        containerRef,
        fontSize,
        isCountdown,
        toggleTimeMode,
        now,
        progress,
        currentHour,
        currentMinute,
        currentSecond,
        lifeProgress,
    };
};
